/**
 * Review + final confirmation (spec §11). Confirmation is an explicit,
 * deliberate locking action — freezes quantities, prevents further
 * scanning, and queues the Mintsoft update — so this screen requires a
 * second, explicit tap-to-confirm (a native confirm() dialog) rather
 * than treating the button press itself as sufficient. confirmSession
 * is idempotent server-side (spec §38/§77 — ALREADY_CONFIRMED on a
 * repeat), so a double-tap here can't double-process even if the
 * confirm() dialog somehow got bypassed twice.
 *
 * Suri (2026-09-30): only a Supervisor/Admin may confirm. The backend
 * (index.ts's confirm route) is the real enforcement — this screen just
 * checks GET /api/me up front so an Operator sees a clear, disabled
 * button with an explanation instead of tapping Confirm and only then
 * discovering it's forbidden via a 403.
 */
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, qs } from '../dom.js';
import type { ReviewLine } from '../types.js';

/**
 * "Confirm this delivery" (2026-10-01) — the instalment-delivery partial
 * push (claude/phase1-partial-receiving-investigation.md). Suri rejected
 * an automatic push after every scan ("by mistake if it is clicked or
 * scan then all will be wrongly updated") and asked for tracking through
 * the ASN flow specifically, not a side channel — so this reuses the
 * SAME Review screen as the final Confirm (same SKU/Expected/Received
 * list the operator already checks), but is a separate, visually
 * distinct button/action that does NOT lock the session: it pushes
 * whatever's been scanned so far to Mintsoft (sellable immediately) and
 * leaves scanning open for later instalments. Same Supervisor/Admin gate
 * as the final Confirm — an accidental tap shouldn't silently reach
 * Mintsoft either.
 */

async function canCurrentUserConfirm(): Promise<{ allowed: boolean; reason?: string }> {
  try {
    const me = await api.me();
    if (!me.active) return { allowed: false, reason: `Operator "${me.name}" is inactive.` };
    if (me.role !== 'SUPERVISOR' && me.role !== 'ADMIN') {
      return { allowed: false, reason: `Only a Supervisor or Admin can confirm (this device is signed in as ${me.name}, an Operator).` };
    }
    return { allowed: true };
  } catch {
    // Fail closed, not open: if we can't determine the role (network
    // blip, unknown operator id), don't offer a Confirm button that the
    // backend would reject anyway — the backend re-checks regardless,
    // but a clear "can't tell right now" beats a confusing 403 later.
    return { allowed: false, reason: "Couldn't verify this device's permissions — check Settings and your connection." };
  }
}

export async function renderReview(container: HTMLElement, sessionId: string): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  container.appendChild(div);
  div.innerHTML = '<p class="hint center">Loading review…</p>';

  let lines: ReviewLine[];
  try {
    lines = await api.review(sessionId);
  } catch (err) {
    div.innerHTML = `<div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p></div><button class="ghost" id="back">ASN List</button>`;
    qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => {
      location.hash = '#/asns';
    });
    return;
  }

  const totalExpected = lines.reduce((a, l) => a + l.expectedQuantity, 0);
  const totalReceived = lines.reduce((a, l) => a + l.receivedQuantity, 0);
  const discrepant = lines.filter((l) => l.variance !== 0).length;
  const confirmPermission = await canCurrentUserConfirm();

  div.innerHTML = `
    <div class="topbar">
      <h1>Review</h1>
    </div>
    <div class="card">
      <p><strong>Expected ${totalExpected}</strong> · <strong>Received ${totalReceived}</strong> · ${discrepant} discrepant line${discrepant === 1 ? '' : 's'}</p>
    </div>
    <input type="text" id="search" class="search-input" placeholder="Search SKU or EAN…" autocapitalize="off" autocorrect="off" spellcheck="false" />
    <div class="list" id="lines"></div>
    ${
      confirmPermission.allowed
        ? ''
        : `<div class="card"><p class="hint">${escapeHtml(confirmPermission.reason ?? 'Confirming requires a Supervisor or Admin.')}</p></div>`
    }
    ${
      confirmPermission.allowed
        ? `<button class="secondary" id="partialConfirmBtn" style="width:100%;margin-top:0.75rem;">Confirm This Delivery</button>
           <p class="hint" style="margin-top:0.4rem;">Pushes what's been scanned so far to Mintsoft so it's sellable now — does NOT lock this receiving. Use this for a partial/instalment delivery; use Confirm Receiving below only once everything has arrived.</p>`
        : ''
    }
    <div class="footer-actions">
      <button class="ghost" id="backBtn">Back to Scanning</button>
      <button class="primary" id="confirmBtn" ${confirmPermission.allowed ? '' : 'disabled title="Only a Supervisor or Admin can confirm"'}>Confirm Receiving</button>
    </div>
  `;

  const linesEl = qs<HTMLDivElement>(div, '#lines');
  const searchInput = qs<HTMLInputElement>(div, '#search');

  function renderLines(filter: string): void {
    const needle = filter.trim().toLowerCase();
    const filtered = needle
      ? lines.filter((l) => l.sku.toLowerCase().includes(needle) || l.ean.toLowerCase().includes(needle))
      : lines;
    linesEl.innerHTML = '';
    if (filtered.length === 0) {
      linesEl.innerHTML = '<p class="hint center">No matching lines.</p>';
      return;
    }
    for (const line of filtered) {
      const row = document.createElement('div');
      row.className = 'review-row';
      const varianceCls = line.variance === 0 ? 'variance-good' : 'variance-bad';
      row.innerHTML = `
        <div>
          <div class="sku">${escapeHtml(line.sku)}</div>
          <div class="ean hint">${escapeHtml(line.ean || 'no barcode')}${line.edited ? ' · edited' : ''} · ${line.scanCount} scan${line.scanCount === 1 ? '' : 's'}</div>
        </div>
        <div class="qty">
          ${line.receivedQuantity}<span class="expected"> / ${line.expectedQuantity}</span>
          <div class="${varianceCls}" style="font-size:0.9rem;">${line.variance === 0 ? 'exact' : (line.variance > 0 ? '+' : '') + line.variance}</div>
        </div>
      `;
      linesEl.appendChild(row);
    }
  }

  searchInput.addEventListener('input', () => renderLines(searchInput.value));
  renderLines('');

  qs<HTMLButtonElement>(div, '#backBtn').addEventListener('click', () => {
    location.hash = `#/scan/${sessionId}`;
  });

  const partialBtn = div.querySelector<HTMLButtonElement>('#partialConfirmBtn');
  partialBtn?.addEventListener('click', async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    if (!confirm("Push what's been scanned so far to Mintsoft? This makes it sellable now but does NOT lock this receiving — you can keep scanning for later instalments.")) return;
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Pushing to Mintsoft…';
    try {
      const outcome = await api.confirmPartial(sessionId);
      if (outcome.kind === 'NOTHING_TO_PUSH') {
        alert('Nothing new to push — everything scanned so far has already been sent to Mintsoft.');
      } else if (outcome.kind === 'SESSION_LOCKED') {
        alert('This receiving has already been fully confirmed, so there is nothing left to partially push.');
      } else {
        const totalQty = outcome.lines.reduce((a, l) => a + l.deltaPushed, 0);
        const lineSummary = outcome.lines.length
          ? `${outcome.lines.length} line(s), ${totalQty} unit(s) pushed to Mintsoft and now sellable.`
          : 'No new quantity to push this time';
        const partBookNote = outcome.partBookSucceeded
          ? ''
          : "\n\nNote: the quantity push succeeded, but updating the ASN's status in Mintsoft failed — try Confirm This Delivery again shortly.";
        alert(`${lineSummary}${partBookNote}`);
      }
    } catch (err) {
      alert(describeError(err));
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  });

  qs<HTMLButtonElement>(div, '#confirmBtn').addEventListener('click', async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const message =
      discrepant > 0
        ? `${discrepant} line(s) don't match expected quantities. Confirming locks this receiving and pushes the final counts to Mintsoft — this cannot be undone through this app (use an Adjustment afterwards for any later correction). Continue?`
        : 'Confirming locks this receiving and pushes the final counts to Mintsoft. Continue?';
    if (!confirm(message)) return;
    btn.disabled = true;
    btn.textContent = 'Confirming…';
    try {
      const outcome = await api.confirm(sessionId);
      location.hash = `#/confirmed/${sessionId}`;
      void outcome;
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Confirm Receiving';
      alert(describeError(err));
    }
  });
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Could not reach the server — check your connection.';
  if (err instanceof ApiError) return `${err.message}${err.code ? ` (${err.code})` : ''}`;
  return String(err);
}
